import {
    ApplicationConfig,
    inject,
    provideAppInitializer,
    provideBrowserGlobalErrorListeners,
    provideZoneChangeDetection
} from '@angular/core';
import {
    provideRouter,
    withComponentInputBinding
} from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { routes } from './app.routes';
import { ConfirmationService, MessageService } from 'primeng/api';
import { APICallInterceptor } from '@core/http/interceptors/apicall/apicall.interceptor';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';
import { definePreset } from '@primeuix/themes';
import { AppConfigService } from '@core/services/app-config/app-config.service';

// Every toast severity looks the same; the icon carries the colour (styles.scss).
const toastSeverity = {
    background: '{surface.100}',
    borderColor: '{surface.300}',
    color: '{surface.900}',
    detailColor: '{surface.600}',
    shadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
    closeButton: { hoverBackground: '{surface.200}', focusRing: { color: '{surface.400}', shadow: 'none' } }
};

const MyPreset = definePreset(Aura, {
    components: {
        // Row hover on every table: a light tint of the theme scale (cells painted in styles.scss).
        datatable: {
            row: {
                hoverBackground: '{primary.200}',
                hoverColor: '{surface.900}'
            }
        },
        // Toasts: compact card in the theme's secondary (surface) colour; severity shows in the icon only.
        toast: {
            root: { width: '22rem', borderRadius: '0.75rem', borderWidth: '1px' },
            icon: { size: '1.125rem' },
            info: toastSeverity,
            success: toastSeverity,
            warn: toastSeverity,
            error: toastSeverity,
            secondary: toastSeverity,
            contrast: toastSeverity,
            content: { padding: '0.75rem 0.875rem', gap: '0.625rem' },
            text: { gap: '0.125rem' },
            summary: { fontWeight: '600', fontSize: '0.875rem' },
            detail: { fontWeight: '400', fontSize: '0.8125rem' },
            closeButton: { width: '1.5rem', height: '1.5rem' },
            closeIcon: { size: '0.75rem' },
        },
        // Addon text ("+6", "RM") must be readable. Aura's default grey fails contrast on white.
        inputgroup: {
            colorScheme: {
                light: {
                    addon: { color: '{surface.600}' }
                }
            }
        },
        // Neutral status pills: a step more definition than Aura's default.
        tag: {
            colorScheme: {
                light: {
                    secondary: { background: '{surface.200}', color: '{surface.700}' }
                }
            }
        }
    },
    semantic: {
        primary: {
            50: '{slate.50}',
            100: '{slate.100}',
            200: '{slate.200}',
            300: '{slate.300}',
            400: '{slate.400}',
            500: '{slate.500}',
            600: '{slate.600}',
            700: '{slate.700}',
            800: '{slate.800}',
            900: '{slate.900}',
            950: '{slate.950}'
        },
        colorScheme: {
            light: {
                // Default Aura maps primary.color to primary.500 (#737373 mid grey).
                // Darken it to the near-black "contrast" look for all primary UI.
                primary: {
                    color: '{primary.950}',
                    contrastColor: '#ffffff',
                    hoverColor: '{primary.800}',
                    activeColor: '{primary.700}'
                }
            }
        }
    }
});

export const appConfig: ApplicationConfig = {
    providers: [
        MessageService,
        ConfirmationService,
        provideRouter(routes, withComponentInputBinding()),
        provideAnimationsAsync(), //Angular 23+ won't support anymore
        provideHttpClient(
            withFetch(), 
            withInterceptors([APICallInterceptor])
        ),
        provideBrowserGlobalErrorListeners(),
        provideZoneChangeDetection({ eventCoalescing: true }),
        providePrimeNG({
            theme: {
                preset: MyPreset,
                options: {
                    darkModeSelector: false //Disable Dark mode
                }
            }
        }),
        /**
         * Load runtime configuration before app starts
         * Configuration is loaded from /assets/config/config.json
         * which is swapped during deployment based on target environment
         */
        provideAppInitializer(() => {
            const appConfigService = inject(AppConfigService);
            return appConfigService.loadConfig();
        })
    ]
};
